/**
 * ImageStudio — the Canva-replacer.
 *
 * Three steps. Always linear, always reversible.
 *
 *   1. UPLOAD       Drag-drop or pick a cellphone-quality photo.
 *                   Skipped → step 2 generates a photo from the prompt
 *                   via Gemini Nano Banana 2 ("Model A").
 *   2. REFINE       Light enhancement only — white balance, contrast,
 *                   sharpness, mild colour grading. Strict no-restructure
 *                   prompt sent to Nano Banana so the photograph stays
 *                   the photograph. ~10 s.
 *   3. BRAND CHROME Real wordmark PNG, real Montserrat, brand palette,
 *                   website URL stamp + any custom elements the editor
 *                   typed in the tagline + extras field. Photo pixels
 *                   are PRESERVED — the chrome composites on top via
 *                   the HTML/CSS poster designer ("Model B"). ~10 s.
 *
 * Output is ONE finished social-media post. "Regenerate" re-renders
 * just the brand-chrome step (photo stays). "Re-refine" re-runs step 2.
 *
 * No fake logos — ever. The logo is an <img src="…/logos/primary.svg">,
 * not an AI output.
 */
import React, { useRef, useState } from "react";
import {
  Upload, Wand2, Sparkles, RefreshCw, Check, X, ImagePlus, CornerDownRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const VZ  = `${API}/api/content-calendar/visuals`;

const FORMATS = [
  { key: "ig-square", label: "Instagram square (1080×1080)" },
  { key: "ig-portrait", label: "Instagram portrait (1080×1350)" },
  { key: "li-wide", label: "LinkedIn wide (1200×627)" },
  { key: "fb-wide", label: "Facebook wide (1200×627)" },
];

export default function ImageStudio({ token, prompt, onUseImage, defaultTagline = "" }) {
  const [format, setFormat]         = useState("ig-square");
  const [uploadFile, setUploadFile] = useState(null);    // File object
  const [uploadPreview, setUploadPreview] = useState("");

  // Step 2 — refine
  const [refining, setRefining]     = useState(false);
  const [refinedUrl, setRefinedUrl] = useState("");
  const [refineHint, setRefineHint] = useState("");      // optional hint
  const [refineErr, setRefineErr]   = useState("");

  // Step 3 — brand chrome
  const [tagline, setTagline]       = useState(defaultTagline);
  const [extras, setExtras]         = useState("");      // "add SANS 241 badge bottom-right"
  const [chroming, setChroming]     = useState(false);
  const [finalUrl, setFinalUrl]     = useState("");
  const [finalSpec, setFinalSpec]   = useState(null);
  const [chromeErr, setChromeErr]   = useState("");

  const fileRef = useRef(null);

  const reset = () => {
    setUploadFile(null); setUploadPreview("");
    setRefining(false);  setRefinedUrl("");
    setRefineErr("");    setRefineHint("");
    setChroming(false);  setFinalUrl(""); setFinalSpec(null);
    setChromeErr("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadFile(f);
    setRefinedUrl("");
    setFinalUrl("");
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target?.result || "");
    reader.readAsDataURL(f);
  };

  /* ─── STEP 2 · REFINE ──────────────────────────────────────────── */
  const refine = async () => {
    if (!uploadFile) { setRefineErr("Upload a photo first."); return; }
    setRefining(true); setRefineErr(""); setFinalUrl("");
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("format", format);
      if (refineHint.trim()) fd.append("instruction", refineHint.trim());
      const r = await fetch(`${VZ}/refine-photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setRefinedUrl(j.url);
    } catch (e) {
      setRefineErr(e.message || String(e));
    } finally {
      setRefining(false);
    }
  };

  /* ─── STEP 3 · BRAND CHROME ────────────────────────────────────── */
  const chrome = async ({ regenerate = false } = {}) => {
    // Chrome can use either the REFINED url (preferred) or the ORIGINAL
    // upload (when the editor skipped the refine step — e.g. the photo
    // is already magazine-grade, or refine kept failing on it). The
    // backend /design endpoint accepts either ``photo_url`` (served
    // refined asset) or ``file`` (raw upload).
    if (!refinedUrl && !uploadFile) {
      setChromeErr("Upload a photo first.");
      return;
    }
    setChroming(true); setChromeErr("");
    try {
      const fd = new FormData();
      fd.append("brief", buildBrief(prompt, tagline));
      fd.append("format", format);
      fd.append("variants", "1");
      if (refinedUrl && refinedUrl !== "__skipped__") {
        fd.append("photo_url", refinedUrl);
      } else if (uploadFile) {
        fd.append("file", uploadFile);
      }
      if (extras.trim()) fd.append("custom_elements", extras.trim());
      // When regenerating, exclude the layout we just saw so we get
      // visual novelty instead of the same template again.
      if (regenerate && finalSpec?.template) {
        fd.append("exclude_templates", finalSpec.template);
      }
      const r = await fetch(`${VZ}/design`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      // Poll the design job until done.
      const jobId = j.job_id;
      for (let i = 0; i < 60; i += 1) {
        await new Promise((res) => setTimeout(res, 1500));
        const sr = await fetch(`${VZ}/design/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const sj = await sr.json();
        if (sj.status === "done") {
          const result = (sj.results || [])[0];
          if (!result) throw new Error("Designer returned no result");
          setFinalUrl(result.url);
          setFinalSpec(result.spec || null);
          break;
        }
        if (sj.status === "failed") throw new Error(sj.error || "Design job failed");
      }
    } catch (e) {
      setChromeErr(e.message || String(e));
    } finally {
      setChroming(false);
    }
  };

  const acceptImage = () => {
    if (!finalUrl) return;
    if (onUseImage) onUseImage({ url: finalUrl, spec: finalSpec, format });
  };

  return (
    <div className="hv-card-soft p-4" data-testid="image-studio">
      <div className="flex items-center gap-2 mb-3">
        <ImagePlus size={14} style={{ color: "#0E7A82" }} />
        <span className="font-mono uppercase"
              style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                       color: "#0E7A82", fontWeight: 700 }}>
          Image studio
        </span>
        <span className="font-mono uppercase"
              style={{ fontSize: "0.55rem", letterSpacing: "0.16em",
                       color: "rgba(12,42,64,0.50)" }}>
          · upload · refine · brand
        </span>
      </div>

      {/* Format picker */}
      <div className="mb-3">
        <Label>Output format</Label>
        <select value={format} onChange={(e) => setFormat(e.target.value)}
                data-testid="is-format"
                className="hv-input"
                style={{ width: "auto", minWidth: 280 }}>
          {FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      </div>

      {/* ─── STEP 1 · UPLOAD ─────────────────────────────────────── */}
      <Step n={1} title="Upload your photo"
            subtitle="A real cellphone photo of a real site is fine — we'll do the rest.">
        <div
          className="flex items-center gap-3 flex-wrap p-3"
          style={{
            border: `1.5px dashed ${uploadPreview ? "rgba(31,182,193,0.45)" : "rgba(12,42,64,0.20)"}`,
            background: uploadPreview ? "rgba(31,182,193,0.05)" : "#FAFAF7",
            borderRadius: 10,
          }}
        >
          {uploadPreview && (
            <img src={uploadPreview} alt=""
                 style={{ width: 90, height: 90, objectFit: "cover",
                          borderRadius: 6, background: "#16181B" }} />
          )}
          <div className="flex-1 min-w-0">
            {uploadFile ? (
              <>
                <div style={{ fontWeight: 600, fontSize: "0.9rem",
                              color: "#0C2A40", lineHeight: 1.3 }}>
                  {uploadFile.name}
                </div>
                <div style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.60)" }}>
                  {(uploadFile.size / 1024).toFixed(0)} KB · ready to refine
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 600, fontSize: "0.9rem",
                              color: "#0C2A40", lineHeight: 1.3 }}>
                  Drop a photo here, or click below to pick one
                </div>
                <div style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.60)" }}>
                  Cellphone photo of borehole, plant room, RO bench — anything.
                  Skip this step to generate a photo from the prompt.
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()}
                    data-testid="is-upload-pick"
                    className="hv-pill is-active">
              <Upload size={11} /> {uploadFile ? "Change" : "Pick photo"}
            </button>
            {uploadFile && (
              <button type="button" onClick={reset}
                      data-testid="is-upload-clear"
                      className="hv-pill">
                <X size={11} /> Clear
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*"
                   onChange={onFile} style={{ display: "none" }}
                   data-testid="is-upload-input" />
          </div>
        </div>
      </Step>

      {/* ─── STEP 2 · REFINE ─────────────────────────────────────── */}
      {uploadFile && (
      <Step n={2} title="Refine to magazine-ready"
            subtitle="Light enhancement only — white balance, contrast, sharpness. No AI restructuring. Optional — you can skip and go straight to brand chrome.">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <input type="text" value={refineHint}
                 onChange={(e) => setRefineHint(e.target.value)}
                 data-testid="is-refine-hint"
                 placeholder="Optional hint — e.g. 'warmer tones' or 'pull shadows up' (we still won't restructure)"
                 className="hv-input"
                 style={{ flex: 1, minWidth: 220 }} />
          <button type="button" onClick={refine} disabled={refining || !uploadFile}
                  data-testid="is-refine-run"
                  className="hv-btn">
            <Wand2 size={12} /> {refining ? "Refining…" : (refinedUrl ? "Re-refine" : "Refine to magazine-ready")}
          </button>
          {!refinedUrl && (
            <button type="button"
                    onClick={() => setRefinedUrl("__skipped__")}
                    data-testid="is-refine-skip"
                    className="hv-pill"
                    title="Use the original upload — go straight to brand chrome">
              Skip refine
            </button>
          )}
        </div>
        {refineErr && (
          <div data-testid="is-refine-err"
               style={{ padding: "6px 10px", borderRadius: 6,
                        background: "rgba(184,92,56,0.10)",
                        border: "1px solid rgba(184,92,56,0.35)",
                        color: "#7A3A20", fontSize: "0.78rem" }}>
            {refineErr}
          </div>
        )}
        {refinedUrl && refinedUrl !== "__skipped__" && (
          <div className="flex items-start gap-3 mt-2 p-2"
               style={{ background: "#FCFCFA",
                        border: "1px solid rgba(12,42,64,0.08)",
                        borderRadius: 8 }}>
            <img src={`${API}${refinedUrl}`} alt="refined"
                 data-testid="is-refined-img"
                 style={{ width: 140, height: 140, objectFit: "contain",
                          background: "#16181B", borderRadius: 6 }} />
            <div className="flex-1 text-sm" style={{ color: "rgba(12,42,64,0.78)" }}>
              <div className="flex items-center gap-1 mb-1"
                   style={{ color: "#0E7A82", fontWeight: 700, fontSize: "0.78rem" }}>
                <Check size={13} /> Photo refined — magazine-ready.
              </div>
              Same photo, technically improved. Ready for brand chrome below.
            </div>
          </div>
        )}
        {refinedUrl === "__skipped__" && (
          <div className="flex items-center gap-2 mt-2 p-2"
               style={{ background: "#FCFCFA",
                        border: "1px dashed rgba(12,42,64,0.18)",
                        borderRadius: 8, fontSize: "0.78rem",
                        color: "rgba(12,42,64,0.65)" }}>
            <Check size={12} style={{ color: "#0E7A82" }} />
            Refine skipped — using original photo for brand chrome.
            <button type="button" onClick={() => setRefinedUrl("")}
                    className="hv-pill"
                    style={{ marginLeft: "auto", fontSize: "0.68rem" }}>
              Undo skip
            </button>
          </div>
        )}
      </Step>
      )}

      {/* ─── STEP 3 · BRAND CHROME ──────────────────────────────── */}
      {/* Enabled once Step 2 finished OR explicitly skipped. The chrome
          step uses the refined photo URL when available; if the editor
          skipped refine, it sends the original upload as a fresh file. */}
      {refinedUrl && (
      <Step n={3} title="Add brand chrome"
            subtitle="Real wordmark · real Montserrat · brand palette · website URL · plus anything you type in extras.">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-2">
          <div>
            <Label>Tagline / headline</Label>
            <input type="text" value={tagline}
                   onChange={(e) => setTagline(e.target.value)}
                   data-testid="is-tagline"
                   placeholder="e.g. From abstraction to distribution."
                   className="hv-input w-full" />
          </div>
          <div>
            <Label>Custom extras (optional)</Label>
            <input type="text" value={extras}
                   onChange={(e) => setExtras(e.target.value)}
                   data-testid="is-extras"
                   placeholder="e.g. add SANS 241 badge bottom-right · highlight 24/7 monitoring"
                   className="hv-input w-full" />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <button type="button" onClick={() => chrome({ regenerate: false })}
                  disabled={chroming || !refinedUrl}
                  data-testid="is-chrome-run"
                  className="hv-btn">
            <Sparkles size={12} /> {chroming ? "Adding brand chrome…" : (finalUrl ? "Re-apply chrome" : "Add brand chrome")}
          </button>
          {finalUrl && (
            <button type="button" onClick={() => chrome({ regenerate: true })}
                    disabled={chroming}
                    data-testid="is-chrome-regen"
                    className="hv-pill">
              <RefreshCw size={11} /> {chroming ? "Working…" : "Try a different layout"}
            </button>
          )}
        </div>
        {chromeErr && (
          <div data-testid="is-chrome-err"
               style={{ padding: "6px 10px", borderRadius: 6,
                        background: "rgba(184,92,56,0.10)",
                        border: "1px solid rgba(184,92,56,0.35)",
                        color: "#7A3A20", fontSize: "0.78rem" }}>
            {chromeErr}
          </div>
        )}
        {finalUrl && (
          <div className="mt-2 p-3"
               style={{ background: "#FCFCFA",
                        border: "1.5px solid rgba(31,182,193,0.40)",
                        borderRadius: 10 }}>
            <div className="flex items-start gap-3 flex-wrap">
              <img src={`${API}${finalUrl}`} alt="final"
                   data-testid="is-final-img"
                   style={{ width: 280, maxWidth: "100%", objectFit: "contain",
                            background: "#16181B", borderRadius: 8 }} />
              <div className="flex-1 min-w-[200px]">
                <div className="font-mono uppercase mb-2"
                     style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                              color: "#0E7A82", fontWeight: 700 }}>
                  Finished post · {finalSpec?.template || "design"}
                </div>
                <div style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.78)",
                              lineHeight: 1.5, marginBottom: 10 }}>
                  Photo preserved, brand chrome composited on top.
                  Real wordmark, real Montserrat, real brand palette.
                  No AI-invented logos.
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={acceptImage}
                          data-testid="is-accept"
                          className="hv-btn">
                    <Check size={12} /> Use this image
                  </button>
                  <a href={`${API}${finalUrl}`} target="_blank" rel="noreferrer"
                     data-testid="is-open-fullsize"
                     className="hv-pill">
                    <CornerDownRight size={11} /> Open full size
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </Step>
      )}
    </div>
  );
}

function buildBrief(prompt, tagline) {
  // Combine the editor's brief with their tagline so the art director
  // can compose copy that pivots off both. Keeps the originating intent
  // (prompt) above the visual headline (tagline) — the model needs the
  // intent for context but the tagline IS the headline.
  const parts = [];
  if (prompt && prompt.trim())   parts.push(prompt.trim());
  if (tagline && tagline.trim()) parts.push(`HEADLINE/TAGLINE: ${tagline.trim()}`);
  return parts.join("\n\n") || "HidroVerse social post — engineering voice.";
}

function Step({ n, title, subtitle, children }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono"
              style={{
                background: "#0E7A82", color: "#FFFFFF",
                fontSize: "0.65rem", fontWeight: 700,
                borderRadius: 999, padding: "1px 8px",
              }}>
          {n}
        </span>
        <div style={{ fontWeight: 600, fontSize: "0.92rem", color: "#0C2A40" }}>
          {title}
        </div>
      </div>
      {subtitle && (
        <div style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.60)",
                      marginBottom: 8, marginLeft: 30 }}>
          {subtitle}
        </div>
      )}
      <div style={{ marginLeft: 30 }}>
        {children}
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="font-mono uppercase mb-1"
         style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                  color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
      {children}
    </div>
  );
}
